import { Component, OnDestroy, OnInit, inject, input, output, viewChild } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { BehaviorSubject, filter, map, skip, Subscription, tap } from 'rxjs';
import { Filters } from '../filters';
import { SearchFilterComponent } from '../search-filter/search-filter.component';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { PropFilterComponent } from '../prop-filter/prop-filter.component';

@Component({
    selector: 'app-filters[propsFilter][searchProps]',
    styles: [':host {display: flex; align-items: center;}'],
    template: `
    <app-search-filter [searchProps]="searchProps()"
      (searchChange)="onSearchChange($event)"
     />
    <button class="ms-2" mat-icon-button (click)="openPropFilterDialog()">
      <mat-icon>filter_list</mat-icon>
    </button>
  `,
    imports: [SearchFilterComponent, MatIconButton, MatIcon]
})
export class FiltersComponent<T> implements OnInit, OnDestroy {
  private propFilterDialog = inject(MatDialog);

  private filters$ = new BehaviorSubject<Filters.Filters>({
    search: '',
    props: '',
  });
  private sub!: Subscription;

  readonly propsFilter = input<Filters.Prop<T>[]>([]);

  readonly searchProps = input<string[]>([]);

  readonly filterQuery = output<string>();

  formValue: any = {};

  ngOnInit(): void {
    const filterSub = this.filters$
      .pipe(
        // remove keys that have empty string
        map((filters) =>
          Object.fromEntries(
            Object.entries(filters).filter(([_, v]) => v != '')
          )
        ),
        map((filters) => Object.values(filters).join('&')),
        map((filters) =>
          filters.endsWith('&') ? filters.slice(0, filters.length - 1) : filters
        ),
        // when component is mounted it will emit an empty string causing the container to fetch again
        skip(1),
        tap(query => this.filterQuery.emit(query))
      )
      .subscribe();

    this.sub = filterSub;
  }

  openPropFilterDialog(): void {
    this.propFilterDialog
      .open(PropFilterDialogComponent, {
        data: { props: this.propsFilter(), formValue: this.formValue },
      })
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        tap(({ currentValue, formValue }) => {
          this.formValue = formValue;
          this.filters$.next({
            ...this.filters$.getValue(),
            props: currentValue,
          });
        })
      )
      .subscribe();
  }

  onSearchChange(search: string) {
    const currentValue = this.filters$.getValue();
    let searchValue = '';
    if (search) {
      searchValue = `filter={${search}}`;
    }
    this.filters$.next({ ...currentValue, search: searchValue });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}

@Component({
    selector: 'app-prop-filter-dialog-component',
    styles: [':host {display: block; min-width: 300px; max-width: 500px}'],
    template: `
    <h2 class="text-center mb-3">Apply filters</h2>
    <app-prop-filter
      #propFilter
      (propsChange)="onPropsChange($event)"
      [props]="data.props"
      [(formValue)]="formValue"
    >
    </app-prop-filter>

    <section class="d-flex justify-content-center align-items-end">
      <button class="fs-2" mat-icon-button (click)="resetForm()">
        <mat-icon>restart_alt</mat-icon>
      </button>

      <button
        color="accent"
        class="ms-3 w-25"
        mat-flat-button
        [mat-dialog-close]="{currentValue, formValue}"
      >
        Ok
      </button>
    </section>
  `,
    imports: [PropFilterComponent, MatIconButton, MatIcon, MatButton, MatDialogClose]
})
export class PropFilterDialogComponent {
  data = inject<{
    props: any;
    formValue: any;
}>(MAT_DIALOG_DATA);

  currentValue: string = '';

  formValue: any;

  readonly propFilter = viewChild<any>('propFilter');

  constructor() {
    const data = this.data;

    this.formValue = data.formValue;
  }

  onPropsChange(props: string) {
    let propsValue = '';
    if (props) {
      propsValue = `filter={${props}}`;
    }
    this.currentValue = propsValue;
  }

  resetForm() {
    this.propFilter()?.resetFilters();
  }
}
